import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Font } from '../common/Styles';
import { detectDevice } from '../utils/deviceDetection';
import { handleiOSClipboard, redirectToAppStore, validateiOSParameters } from '../utils/iOSClipboardHandler';
import { handleAndroidReferrerRedirect } from '../utils/androidReferrerRedirect';
import { storeFingerprint } from '../utils/deviceFingerprint';
import { getQRCode } from '../actions';

const DeepLinkHandler = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState('Preparing your app...');
  const [showDesktopOptions, setShowDesktopOptions] = useState(false);
  const appStoreURL = useSelector(state => state.ios);
  const playStoreURL = useSelector(state => state.android);
  const qrCode = useSelector(state => state.qrCode);
  const title = useSelector(state => state.title);
  const description = useSelector(state => state.description);

  // Fetch app store URLs from API on component mount
  useEffect(() => {
    dispatch(getQRCode());
  }, [dispatch]);

  useEffect(() => {
    const handleDeepLink = async () => {
      // Wait for API data to be available
      if (!appStoreURL || !playStoreURL) {
        return; // Still loading API data
      }

      // Extract parameters from URL
      const urlParams = new URLSearchParams(window.location.search);
      const i = urlParams.get('i');
      const b = urlParams.get('b');

      if (!i || !b) {
        setMessage('Error: Invalid link parameters. Missing required parameters.');
        setIsLoading(false);
        return;
      }

      // Device detection using utility
      const device = detectDevice();
      
      // Store device fingerprint for mobile devices and wait for completion
      if (device.isMobile) {
        const currentUrl = window.location.href;
        const backendUrl = ''; // Use proxy - empty string means same origin
        
        try {
          console.log('📡 Storing device fingerprint...');
          await storeFingerprint(currentUrl, backendUrl);
          console.log('✅ Device fingerprint stored, proceeding with flow');
        } catch (error) {
          console.warn('⚠️ Fingerprint storage failed, continuing anyway:', error);
        }
      }
      
      setIsLoading(false);

      if (device.isIOS) {
        await handleiOSDeepLinkFlow(i, b);
      } else if (device.isAndroid) {
        handleAndroidDeepLinkFlow(i, b);
      } else {
        // Desktop or other devices - show app download options
        handleDesktopDeepLinkFlow(i, b);
      }
    };

    handleDeepLink();
  }, [appStoreURL, playStoreURL]);

  const handleDesktopDeepLinkFlow = (i, b) => {
    setMessage('Get the app to continue');
    setShowDesktopOptions(true);
    // Store deferred deep link data for when app is installed
    const deferredData = {
      i,
      b,
      timestamp: Date.now(),
      url: window.location.href
    };
    
    // Store in localStorage for deferred deep linking
    try {
      localStorage.setItem('nivoDeepLinkData', JSON.stringify(deferredData));
      console.log('Deferred deep link data stored:', deferredData);
    } catch (error) {
      console.warn('Failed to store deferred deep link data:', error);
    }
  };

  const handleiOSDeepLinkFlow = async (i, b) => {
    // Validate parameters
    const validation = validateiOSParameters(i, b);
    if (!validation.valid) {
      setMessage(`Error: ${validation.error}`);
      return;
    }

    // Show custom app details instead of generic message
    setMessage('');
    setIsLoading(false);
  };
  const handleiOSSetupClick = async () => {
    // Extract parameters from URL
    const urlParams = new URLSearchParams(window.location.search);
    const i = urlParams.get('i');
    const b = urlParams.get('b');

    setMessage('Setting up your app...');
    setIsLoading(true);
    
    try {
      const result = await handleiOSClipboard(i, b);
      setMessage(result.message);
      setIsLoading(false);
      
      if (result.success) {
        // Redirect to App Store after 1 second
        redirectToAppStore(appStoreURL, 1000);
      } else {
        // Fallback: still redirect to App Store
        redirectToAppStore(appStoreURL, 1000);
      }
    } catch (error) {
      console.error('iOS deep link handling failed:', error);
      setMessage('Setup failed. Opening App Store...');
      setIsLoading(false);
      redirectToAppStore(appStoreURL, 1000);
    }
  };

  const handleAndroidDeepLinkFlow = (i, b) => {
    setMessage('Opening Play Store...');
    
    const result = handleAndroidReferrerRedirect(i, b, playStoreURL);
    
    if (result.message) {
      setMessage(result.message);
    }
    
    if (!result.success && result.error) {
      console.error('Android redirect failed:', result.error);
      if (!result.fallback) {
        setMessage('Redirect failed');
      }
    }
  };


  const device = detectDevice();
  const showiOSSetupButton = device.isIOS && !isLoading && message === '';
  
  return (
    <div style={styles.container}>
      <div style={styles.contentContainer}>
        {isLoading && <div style={styles.spinner} />}
        <p style={styles.message}>{message}</p>
        
        {showiOSSetupButton && (
          <div style={styles.appDetailsContainer}>
            <h2 style={styles.appTitle}>{title}</h2>
            <p style={styles.appDescription}>{description}</p>
            <button type="button" onClick={handleiOSSetupClick} style={styles.setupButton}>
              Open App
            </button>
          </div>
        )}
        
        {showDesktopOptions && (
          <div style={styles.downloadOptions}>
            <h5 style={styles.downloadTitle}>Hold your smartphone camera over the QR code to get the app</h5>            
            <div style={styles.qrCodeSection}>
              <p style={styles.qrText}>Or scan with your phone:</p>
              {qrCode && (
                <img 
                  src={`data:image/png;base64,${qrCode}`} 
                  alt="QR code" 
                  width="300" 
                  height="300" 
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    minHeight: '100vh',
    // backgroundColor: '#f5f5f5',
    padding: '80px 20px 20px 20px',
  },
  contentContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '40px',
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    maxWidth: '600px',
    width: '100%',
    textAlign: 'center',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #3b9eb3',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: '20px',
  },
  message: {
    ...Font,
    fontSize: '18px',
    color: '#333',
    margin: '0 0 30px 0',
    fontWeight: '500',
  },
  downloadOptions: {
    width: '100%',
  },
  downloadTitle: {
    ...Font,
    fontSize: '18px',
    color: '#333',
    margin: '0 0 10px 0',
    fontWeight: '600',
  },
  downloadSubtitle: {
    ...Font,
    fontSize: '16px',
    color: '#666',
    margin: '0 0 30px 0',
    lineHeight: '1.4',
  },
  storeButtons: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
    marginBottom: '30px',
  },
  storeButton: {
    display: 'inline-block',
    padding: '15px 30px',
    backgroundColor: '#3b9eb3',
    color: 'white',
    textDecoration: 'none',
    borderRadius: '8px',
    transition: 'background-color 0.3s ease',
    border: 'none',
    cursor: 'pointer',
  },
  storeButtonText: {
    ...Font,
    fontSize: '16px',
    fontWeight: '500',
  },
  qrCodeSection: {
    borderTop: '1px solid #eee',
    paddingTop: '20px',
  },
  qrText: {
    ...Font,
    fontSize: '14px',
    color: '#666',
    margin: '0 0 10px 0',
  },
  qrLink: {
    ...Font,
    fontSize: '16px',
    color: '#3b9eb3',
    textDecoration: 'none',
    fontWeight: '500',
  },
  setupButton: {
    padding: '15px 30px',
    backgroundColor: '#3b9eb3',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '18px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease',
    marginTop: '20px',
    ...Font,
  },
  appDetailsContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
  },
  appLogo: {
    marginBottom: '20px',
  },
  appTitle: {
    ...Font,
    fontSize: '28px',
    fontWeight: '600',
    color: '#333',
    margin: '0 0 15px 0',
    textAlign: 'center',
  },
  appDescription: {
    ...Font,
    fontSize: '16px',
    color: '#666',
    textAlign: 'center',
    lineHeight: '1.5',
    margin: '0 0 30px 0',
    maxWidth: '400px',
  },
};

// Add CSS animation for spinner
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);

export { DeepLinkHandler };
